import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Stock } from '../models/stock.model';
import { BaseComponentDirective } from '../../base/base.component';
import { KeyValuePair } from '../../models/key-value-pair.model';

@Component({
  selector: 'app-non-runner-modal',
  templateUrl: './non-runner-modal.component.html',
  styleUrls: ['./non-runner-modal.component.scss']
})
export class NonRunnerModalComponent extends BaseComponentDirective
  implements OnInit {
  change = new EventEmitter<UntypedFormGroup>();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal
  ) {
    super();
  }

  @Input() nonRunners: Stock[] = [];

  currentIndex = 0;

  doesCarStartGroup!: UntypedFormGroup;
  dashboardWarningLightsGroup!: UntypedFormGroup;
  oilTemperatureLightGroup!: UntypedFormGroup;
  roadworthyGroup!: UntypedFormGroup;
  truckAccessibleGroup!: UntypedFormGroup;
  additionalNotesGroup!: UntypedFormGroup;

  yesNoOptions: KeyValuePair[] = [
    { key: 'true', value: 'Yes' },
    { key: 'false', value: 'No' }
  ];

  ngOnInit(): void {

    this.doesCarStartGroup = this.createYesNoQuestion(
      'doesCarStart',
      'Does the car start?'
    );

    this.dashboardWarningLightsGroup = this.createYesNoQuestion(
      'dashboardWarningLights',
      'Are there any red or amber warning lights on the dashboard?'
    );

    this.oilTemperatureLightGroup = this.createYesNoQuestion(
      'oilTemperatureLight',
      'Is the oil temperature light illuminated?'
    );

    this.roadworthyGroup = this.createYesNoQuestion(
      'roadworthy',
      'Is the vehicle roadworthy and mechanically sound for a plate driver to drive safely?'
    );

    this.truckAccessibleGroup = this.createYesNoQuestion(
      'truckAccessible',
      'Is the vehicle accessible by a truck?'
    );

    this.additionalNotesGroup = this.createTextQuestion(
      'additionalNotes',
      'Additional Notes'
    );

    this.loadCurrentResponse();
  }

  onAnswerChange($event) {
     const questionName = $event?.get?.('name')?.value;

  if (questionName === 'doesCarStart') {

    const starts = this.getBoolean(this.doesCarStartGroup);

    if (starts === false) {
      this.dashboardWarningLightsGroup.get('value')?.setValue(null);
      this.oilTemperatureLightGroup.get('value')?.setValue(null);
      this.roadworthyGroup.get('value')?.setValue(null);
    }
  }

    this.change.emit($event);
  }

  private createYesNoQuestion(name: string, question: string): UntypedFormGroup {

    return this.fb.group({
      name: [name],
      question: [question],
      explanation: [''],
      value: [null],
      disabled: [false],
      encrypted: [false],
      mask: [''],
      formatType: [0],
      minValue: [null],
      maxValue: [null],
      isHidden: [false],
      readOnly: [false],
      informedAnswer: ['']
    });

  }

  private createTextQuestion(name: string, question: string): UntypedFormGroup {

    return this.fb.group({
      name: [name],
      question: [question],
      explanation: [''],
      value: [''],
      disabled: [false],
      encrypted: [false],
      mask: [''],
      formatType: [2],
      minValue: [null],
      maxValue: [null],
      isHidden: [false],
      dataType: ['text'],
      keyboardType: ['text'],
      disableCopyPaste: [false],
      warning: [null]
    });

  }
  get nonRunner(): Stock {
    return this.nonRunners[this.currentIndex];
  }

  get isFirst(): boolean {
    return this.currentIndex === 0;
  }

  get isLast(): boolean {
    return this.currentIndex === this.nonRunners.length - 1;
  }

  private getBoolean(group: UntypedFormGroup): boolean | null {

    const value = group.controls.value.value;

    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }

    return null;
  }

  get showDashboardWarningQuestion(): boolean {
    return this.getBoolean(this.doesCarStartGroup) === true;
  }

  get showOilTemperatureQuestion(): boolean {
    return this.getBoolean(this.dashboardWarningLightsGroup) === true;
  }

  get showRoadworthyQuestion(): boolean {

    return this.getBoolean(this.dashboardWarningLightsGroup) === false
      || this.getBoolean(this.oilTemperatureLightGroup) === false;

  }

  get showTruckAccessibleQuestion(): boolean {
    return this.getBoolean(this.doesCarStartGroup) === false;
  }

  get showAdditionalNotes(): boolean {

    return this.getBoolean(this.truckAccessibleGroup) !== null
      || this.getBoolean(this.roadworthyGroup) !== null
      || this.getBoolean(this.oilTemperatureLightGroup) === true;

  }

  get visibleQuestions(): UntypedFormGroup[] {

    const questions: UntypedFormGroup[] = [];

    questions.push(this.doesCarStartGroup);

    if (this.showDashboardWarningQuestion) {
      questions.push(this.dashboardWarningLightsGroup);
    }

    if (this.showOilTemperatureQuestion) {
      questions.push(this.oilTemperatureLightGroup);
    }

    if (this.showRoadworthyQuestion) {
      questions.push(this.roadworthyGroup);
    }

    if (this.showTruckAccessibleQuestion) {
      questions.push(this.truckAccessibleGroup);
    }

    if (this.showAdditionalNotes) {
      questions.push(this.additionalNotesGroup);
    }

    return questions;
  }

  getQuestionNumber(group: UntypedFormGroup): number {
    return this.visibleQuestions.indexOf(group) + 1;
  }

  get canContinue(): boolean {

    const starts = this.getBoolean(this.doesCarStartGroup);

    if (starts === null) {
      return false;
    }

    if (!starts) {
      return this.getBoolean(this.truckAccessibleGroup) !== null;
    }

    const warnings = this.getBoolean(this.dashboardWarningLightsGroup);

    if (warnings === null) {
      return false;
    }

    if (!warnings) {
      return this.getBoolean(this.roadworthyGroup) !== null;
    }

    const oil = this.getBoolean(this.oilTemperatureLightGroup);

    if (oil === null) {
      return false;
    }

    if (oil) {
      return true;
    }

    return this.getBoolean(this.roadworthyGroup) !== null;
  }

  next(): void {

    if (!this.canContinue) {
      return;
    }

    this.saveCurrentResponse();

    if (!this.isLast) {
      this.currentIndex++;
      this.loadCurrentResponse();
    }
    else {
      this.activeModal.close(this.nonRunners);
    }
  }

  dismissModal(): void {
    this.activeModal.dismiss();
  }

  private saveCurrentResponse(): void {

    this.nonRunner.nonRunnerDetails = {
      doesCarStart: this.getBoolean(this.doesCarStartGroup),
      dashboardWarningLights: this.getBoolean(this.dashboardWarningLightsGroup),
      oilTemperatureLight: this.getBoolean(this.oilTemperatureLightGroup),
      roadworthy: this.getBoolean(this.roadworthyGroup),
      truckAccessible: this.getBoolean(this.truckAccessibleGroup),
      additionalNotes: this.additionalNotesGroup.controls.value.value
    };


  }

  private loadCurrentResponse(): void {

    const details = this.nonRunner.nonRunnerDetails;

    this.doesCarStartGroup.controls.value.setValue(
      details?.doesCarStart == null ? null : details.doesCarStart.toString()
    );

    this.dashboardWarningLightsGroup.controls.value.setValue(
      details?.dashboardWarningLights == null ? null : details.dashboardWarningLights.toString()
    );

    this.oilTemperatureLightGroup.controls.value.setValue(
      details?.oilTemperatureLight == null ? null : details.oilTemperatureLight.toString()
    );

    this.roadworthyGroup.controls.value.setValue(
      details?.roadworthy == null ? null : details.roadworthy.toString()
    );

    this.truckAccessibleGroup.controls.value.setValue(
      details?.truckAccessible == null ? null : details.truckAccessible.toString()
    );

    this.additionalNotesGroup.controls.value.setValue(
      details?.additionalNotes ?? ''
    );

  }

}
